import React, { useEffect, useRef, useState } from 'react';
import { X, Mail, AlertCircle, CheckCircle, RefreshCw } from 'lucide-react';
import { userAPI } from '../services/api';

interface VerifyEmailOtpModalProps {
  isOpen: boolean;
  email: string;
  token: string;
  /** When true, a code was already sent (e.g. right after register). */
  codeAlreadySent?: boolean;
  destinationMasked?: string;
  expiresInMinutes?: number;
  initialResendCooldownSeconds?: number;
  allowSkip?: boolean;
  onVerified: () => void;
  onSkip?: () => void;
  onClose: () => void;
}

const CODE_LENGTH = 6;

const VerifyEmailOtpModal: React.FC<VerifyEmailOtpModalProps> = ({
  isOpen,
  email,
  token,
  codeAlreadySent = false,
  destinationMasked,
  expiresInMinutes = 10,
  initialResendCooldownSeconds = 60,
  allowSkip = true,
  onVerified,
  onSkip,
  onClose,
}) => {
  const [digits, setDigits] = useState<string[]>(Array(CODE_LENGTH).fill(''));
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [cooldown, setCooldown] = useState(
    codeAlreadySent ? initialResendCooldownSeconds : 0
  );
  const [masked, setMasked] = useState(destinationMasked || email);
  const [ttlMinutes, setTtlMinutes] = useState(expiresInMinutes);
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const autoSentRef = useRef(false);
  const onVerifiedRef = useRef(onVerified);
  onVerifiedRef.current = onVerified;

  useEffect(() => {
    if (!isOpen) return;
    setDigits(Array(CODE_LENGTH).fill(''));
    setError(null);
    setSuccess(false);
    setMasked(destinationMasked || email);
    setTtlMinutes(expiresInMinutes);
    setCooldown(codeAlreadySent ? initialResendCooldownSeconds : 0);
    autoSentRef.current = false;
  }, [isOpen, email, destinationMasked, expiresInMinutes, codeAlreadySent, initialResendCooldownSeconds]);

  // Auto-send when opening for login / paths where a code was not just issued.
  useEffect(() => {
    if (!isOpen || codeAlreadySent || autoSentRef.current) return;
    autoSentRef.current = true;

    let cancelled = false;
    const send = async () => {
      setIsSending(true);
      setError(null);
      try {
        const result = await userAPI.sendOtp(token, {
          channel: 'email',
          purpose: 'email_verify',
        });
        if (cancelled) return;
        if (result.alreadyVerified) {
          setSuccess(true);
          onVerifiedRef.current();
          return;
        }
        if (result.destinationMasked) setMasked(result.destinationMasked);
        if (result.expiresInMinutes) setTtlMinutes(result.expiresInMinutes);
        setCooldown(result.resendAvailableInSeconds ?? initialResendCooldownSeconds);
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.message || 'Failed to send verification code');
        }
      } finally {
        if (!cancelled) setIsSending(false);
      }
    };

    void send();
    return () => {
      cancelled = true;
    };
  }, [isOpen, codeAlreadySent, token, initialResendCooldownSeconds]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = window.setInterval(() => {
      setCooldown((c) => (c <= 1 ? 0 : c - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [cooldown]);

  if (!isOpen) return null;

  const code = digits.join('');

  const handleDigitChange = (index: number, value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (!cleaned) {
      const next = [...digits];
      next[index] = '';
      setDigits(next);
      return;
    }

    // Support paste of full code into any box
    if (cleaned.length > 1) {
      const chars = cleaned.slice(0, CODE_LENGTH).split('');
      const next = Array(CODE_LENGTH).fill('');
      chars.forEach((ch, i) => {
        next[i] = ch;
      });
      setDigits(next);
      const focusIdx = Math.min(chars.length, CODE_LENGTH) - 1;
      inputsRef.current[focusIdx]?.focus();
      return;
    }

    const next = [...digits];
    next[index] = cleaned;
    setDigits(next);
    if (index < CODE_LENGTH - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handleResend = async (enforceUiCooldown = true) => {
    if (enforceUiCooldown && cooldown > 0) return;
    setIsSending(true);
    setError(null);
    try {
      const result = await userAPI.sendOtp(token, {
        channel: 'email',
        purpose: 'email_verify',
      });
      if (result.alreadyVerified) {
        setSuccess(true);
        onVerified();
        return;
      }
      if (result.destinationMasked) setMasked(result.destinationMasked);
      if (result.expiresInMinutes) setTtlMinutes(result.expiresInMinutes);
      setCooldown(result.resendAvailableInSeconds ?? initialResendCooldownSeconds);
    } catch (err: any) {
      setError(err?.message || 'Failed to send verification code');
    } finally {
      setIsSending(false);
    }
  };

  const handleVerify = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (code.length !== CODE_LENGTH) {
      setError(`Enter the ${CODE_LENGTH}-digit code from your email.`);
      return;
    }
    setIsVerifying(true);
    setError(null);
    try {
      await userAPI.verifyOtp(token, {
        code,
        channel: 'email',
        purpose: 'email_verify',
      });
      setSuccess(true);

      // Update stored user / org verified flag
      const storedUser = localStorage.getItem('piezit-user');
      if (storedUser) {
        try {
          const parsed = JSON.parse(storedUser);
          parsed.verified = true;
          localStorage.setItem('piezit-user', JSON.stringify(parsed));
        } catch {
          /* ignore */
        }
      }
      const storedOrg = localStorage.getItem('piezit-organization');
      if (storedOrg) {
        try {
          const parsed = JSON.parse(storedOrg);
          parsed.verified = true;
          localStorage.setItem('piezit-organization', JSON.stringify(parsed));
        } catch {
          /* ignore */
        }
      }

      onVerified();
    } catch (err: any) {
      setError(err?.message || 'Incorrect or expired code');
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Verify your email</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleVerify} className="p-6 space-y-5">
          <div className="flex items-start gap-3 bg-primary-50 border border-primary-100 rounded-lg p-4">
            <Mail className="w-5 h-5 text-primary-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-gray-700">
              <p className="font-medium text-gray-900 mb-1">Check your inbox</p>
              <p>
                We sent a {CODE_LENGTH}-digit code to <strong>{masked}</strong>.
                It expires in {ttlMinutes} minutes.
              </p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Verification code
            </label>
            <div className="flex justify-between gap-2">
              {digits.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => {
                    inputsRef.current[index] = el;
                  }}
                  type="text"
                  inputMode="numeric"
                  autoComplete={index === 0 ? 'one-time-code' : 'off'}
                  maxLength={CODE_LENGTH}
                  value={digit}
                  onChange={(e) => handleDigitChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  className="w-11 h-12 text-center text-lg font-semibold border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  aria-label={`Digit ${index + 1}`}
                />
              ))}
            </div>
          </div>

          {error && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div className="flex items-start gap-2 bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700">
              <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>Email verified successfully.</span>
            </div>
          )}

          <button
            type="submit"
            disabled={isVerifying || code.length !== CODE_LENGTH || success}
            className="w-full px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isVerifying ? 'Verifying…' : 'Verify email'}
          </button>

          <div className="flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              onClick={() => handleResend(true)}
              disabled={isSending || cooldown > 0}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className={`w-4 h-4 ${isSending ? 'animate-spin' : ''}`} />
              {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
            </button>
            {allowSkip && onSkip && (
              <button
                type="button"
                onClick={onSkip}
                className="flex-1 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-lg transition-colors duration-200 font-medium"
              >
                Skip for now
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default VerifyEmailOtpModal;
